import type { AutomationAreaRunDto, AutomationCatalogDto } from '@tms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api-client';
import { queryKeys } from '../../../lib/query-keys';

const catalogKey = [...queryKeys.automation.all, 'catalog'] as const;

/** Every scenario of every repository, filed by product team and area, with its last result. */
export const useCatalog = () =>
  useQuery({
    queryKey: catalogKey,
    queryFn: ({ signal }) =>
      api.get<AutomationCatalogDto>('/automation/catalog', undefined, signal),
    // GitLab is asked for each repository's latest reports; a minute is fresh enough
    staleTime: 60_000,
  });

/** Admins: moves a scenario to another area; null returns it to where its tags put it. */
export const useMoveScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      className,
      areaId,
    }: {
      projectId: string;
      className: string;
      areaId: string | null;
    }) =>
      api.put<{ ok: true }>(
        `/automation/catalog/scenarios/${encodeURIComponent(projectId)}/${encodeURIComponent(className)}/area`,
        { areaId },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKey }),
  });
};

/** Runs a folder: one pipeline per repository its scenarios live in. */
export const useRunArea = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (areaId: string) =>
      api.post<AutomationAreaRunDto[]>(
        `/automation/catalog/areas/${encodeURIComponent(areaId)}/run`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.automation.all }),
  });
};
